import { SampleDto, StatusDto } from '@/api/ApiClient';

/**
 * Whether a sample DTO may receive live tree mutations (add activity, transfer, submit, delete).
 *
 * Read-only trees use dedicated node types (`sampleAggregate`, `sampleSnapshot`, `sampleInPackage`) with
 * reload-only registries. This guard covers paths that still evaluate a `SampleDto` against the live
 * `sample` registry or toolbar (`SampleTreeArea`, owning-sample lookup on `sampleActivity`).
 *
 * Published `/samples/{id}` representatives boot as `sampleAggregate`, not `sample`, but the DTO keeps
 * `isSnapshot: true`. That flag is enough here — no separate aggregate check — and matches package
 * snapshot copies. Live inventory rows have `isSnapshot !== true`.
 */
export function sampleAllowsTreeMutations(sample: { isSnapshot?: boolean } | null | undefined): boolean {
  return sample != null && sample.isSnapshot !== true;
}

/**
 * Whether the sample is in Published status (no new activities per API).
 */
function isPublishedSample(sample: SampleDto): boolean {
  return sample.status === StatusDto.Published;
}

/**
 * Mirrors server SampleActivityCreateRequestValidator lifecycle rules for **new processing**
 * activities created from **Start Processing** in the sample tree — preparation, synthesis, and
 * post-treatment only (split/dicing uses a separate flow).
 */
export function canStartNewProcessingActivity(sample: SampleDto | null | undefined): boolean {
  return getNewProcessingActivityBlockedReason(sample) === null;
}

/**
 * Mirrors server rules for **new characterization** (category CHAR). When the sample is shipped,
 * only external facility is allowed — the dialog enforces facility; we still allow opening it.
 */
export function canStartNewCharacterizationActivity(sample: SampleDto | null | undefined): boolean {
  return getNewCharacterizationActivityBlockedReason(sample) === null;
}

/** Null when starting processing is allowed; otherwise a short reason for tooltips. */
export function getNewProcessingActivityBlockedReason(sample: SampleDto | null | undefined): string | null {
  if (!sample) {
    return 'Sample is not loaded.';
  }
  if (!sampleAllowsTreeMutations(sample)) {
    return 'Cannot add activities to a snapshot.';
  }
  if (isPublishedSample(sample)) {
    return 'Cannot add activities to a published sample.';
  }
  if (sample.finalized === true) {
    return 'Cannot add processing to a split sample; continue on a piece.';
  }
  if (sample.shipped === true) {
    return 'Cannot add processing to a shipped sample.';
  }
  if (sample.status === StatusDto.Submitted) {
    return 'Cannot add preparation, synthesis, or post-treatment after the sample is submitted.';
  }
  return null;
}

/** Null when starting characterization is allowed; otherwise a short reason for tooltips. */
export function getNewCharacterizationActivityBlockedReason(sample: SampleDto | null | undefined): string | null {
  if (!sample) {
    return 'Sample is not loaded.';
  }
  if (!sampleAllowsTreeMutations(sample)) {
    return 'Cannot add activities to a snapshot.';
  }
  if (isPublishedSample(sample)) {
    return 'Cannot add activities to a published sample.';
  }
  if (sample.finalized === true) {
    return 'Cannot add characterization to a split sample; continue on a piece.';
  }
  return null;
}

/** Extra hint when characterization is allowed but the sample is shipped (external facility only per API). */
export function getCharacterizationShippedHint(sample: SampleDto | null | undefined): string | null {
  if (!sample?.shipped) {
    return null;
  }
  return 'For shipped samples, use an external facility.';
}

/**
 * Facility choices for characterization UI.
 * When the owning sample is **shipped**, **new** characterization is external-only (API rule).
 * Existing internal activities created before shipping keep the full facility list so they remain editable.
 */
export function getCharacterizationFacilityChoicesForSample(
  sample: SampleDto | null | undefined,
  configuredFacilities: readonly string[] | undefined,
  options?: { currentFacility?: string | null }
): string[] {
  const allChoices = [...(configuredFacilities ?? []), 'External'];

  if (sample?.shipped === true) {
    const current = options?.currentFacility?.trim();
    if (current && current !== 'External') {
      return allChoices.includes(current) ? allChoices : [current, ...allChoices];
    }
    return ['External'];
  }

  return allChoices;
}
